import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { FileUploader } from "@/components/site/FileUploader";
import { useAuth } from "@/lib/auth";
import { api, fileUrl } from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const SECTIONS = [
    { key: "overview", label: "Overview", icon: "LayoutDashboard" },
    { key: "homepage", label: "Homepage", icon: "Home" },
    { key: "about", label: "About", icon: "Info" },
    { key: "committee", label: "Committee", icon: "Users" },
    { key: "societies", label: "Societies", icon: "Building2" },
    { key: "documents", label: "Documents", icon: "FileText" },
    { key: "gallery", label: "Gallery", icon: "Image" },
    { key: "events", label: "Events", icon: "Calendar" },
    { key: "news", label: "News", icon: "Newspaper" },
    { key: "submissions", label: "Messages", icon: "Inbox" },
    { key: "settings", label: "Settings", icon: "Settings" },
];

export default function AdminDashboard() {
    const { user, loading, logout } = useAuth();
    const [section, setSection] = useState("overview");
    const nav = useNavigate();

    if (loading) return <div className="grid min-h-screen place-items-center text-slate-400">Loading…</div>;
    if (!user) return <Navigate to="/admin/login" replace />;

    return (
        <div className="grid min-h-screen bg-slate-50 lg:grid-cols-[260px_1fr]">
            <aside className="hidden border-r border-slate-200 bg-white lg:flex lg:flex-col">
                <div className="flex items-center gap-3 border-b border-slate-200 p-5">
                    <Logo size={38} />
                    <div>
                        <div className="font-serif text-lg font-semibold text-brand-blue">Gaurav Sankalp</div>
                        <div className="text-[10px] font-medium uppercase tracking-[0.22em] text-slate-500">Admin Panel</div>
                    </div>
                </div>
                <nav className="flex-1 space-y-1 p-3">
                    {SECTIONS.map((s) => {
                        const I = Icons[s.icon] || Icons.Circle;
                        return (
                            <button
                                key={s.key}
                                onClick={() => setSection(s.key)}
                                data-testid={`admin-nav-${s.key}`}
                                className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm ${section === s.key ? "bg-brand-blue text-white" : "text-slate-700 hover:bg-slate-100"}`}
                            >
                                <I size={16} /> {s.label}
                            </button>
                        );
                    })}
                </nav>
                <div className="border-t border-slate-200 p-3">
                    <Link to="/" className="btn-ghost w-full !justify-start" data-testid="admin-view-site"><Icons.ExternalLink size={14} /> View site</Link>
                    <button onClick={() => { logout(); nav("/admin/login"); }} className="btn-ghost mt-1 w-full !justify-start" data-testid="admin-logout">
                        <Icons.LogOut size={14} /> Logout
                    </button>
                </div>
            </aside>

            <main className="min-w-0">
                <header className="flex items-center justify-between border-b border-slate-200 bg-white/70 px-6 py-4 backdrop-blur-md">
                    <div className="lg:hidden flex items-center gap-3">
                        <Logo size={32} />
                        <span className="font-serif text-lg text-brand-blue">Admin</span>
                    </div>
                    <div className="hidden lg:block">
                        <h1 className="font-serif text-2xl text-slate-900 capitalize">{SECTIONS.find((s) => s.key === section)?.label}</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <select
                            value={section}
                            onChange={(e) => setSection(e.target.value)}
                            className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm lg:hidden"
                            data-testid="admin-mobile-nav"
                        >
                            {SECTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </select>
                        <span className="hidden rounded-full bg-brand-blue-light px-3 py-1 text-xs font-medium text-brand-blue sm:inline">
                            {user.username}
                        </span>
                        <button onClick={() => { logout(); nav("/admin/login"); }} className="btn-ghost lg:hidden" data-testid="admin-logout-mobile"><Icons.LogOut size={14} /></button>
                    </div>
                </header>

                <div className="p-6">
                    {section === "overview" && <Overview />}
                    {section === "homepage" && <HomepageEditor />}
                    {section === "about" && <AboutEditor />}
                    {section === "committee" && <CommitteeManager />}
                    {section === "societies" && <SocietiesManager />}
                    {section === "documents" && <DocumentsManager />}
                    {section === "gallery" && <GalleryManager />}
                    {section === "events" && <EventsManager />}
                    {section === "news" && <NewsManager />}
                    {section === "submissions" && <SubmissionsInbox />}
                    {section === "settings" && <SettingsEditor />}
                </div>
            </main>
        </div>
    );
}

// -------- OVERVIEW --------
const Overview = () => {
    const [stats, setStats] = useState(null);
    useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
    const cards = [
        { k: "societies", label: "Member Societies", icon: "Building2", color: "bg-brand-blue" },
        { k: "documents", label: "Documents", icon: "FileText", color: "bg-brand-orange" },
        { k: "events", label: "Events", icon: "Calendar", color: "bg-emerald-500" },
        { k: "news", label: "News", icon: "Newspaper", color: "bg-purple-500" },
        { k: "gallery_albums", label: "Gallery Albums", icon: "Image", color: "bg-pink-500" },
        { k: "committee", label: "Committee", icon: "Users", color: "bg-amber-500" },
        { k: "unread_submissions", label: "Unread Messages", icon: "Inbox", color: "bg-red-500" },
        { k: "submissions", label: "Total Messages", icon: "Mail", color: "bg-slate-700" },
    ];
    return (
        <div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="admin-stats">
                {cards.map((c) => {
                    const I = Icons[c.icon] || Icons.Circle;
                    return (
                        <div key={c.k} className="surface-card p-5">
                            <div className={`grid h-10 w-10 place-items-center rounded-xl text-white ${c.color}`}><I size={18} /></div>
                            <div className="mt-4 text-3xl font-semibold text-slate-900">{stats?.[c.k] ?? "—"}</div>
                            <div className="text-xs text-slate-500">{c.label}</div>
                        </div>
                    );
                })}
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-2">
                <QuickLink title="Manage Societies" desc="Add or edit member societies, upload logos and covers." icon="Building2" section="societies" />
                <QuickLink title="Publish a Notice" desc="Post a new notice, tender or circular." icon="Megaphone" section="documents" />
                <QuickLink title="Add Committee Member" desc="Update the association committee." icon="Users" section="committee" />
                <QuickLink title="Contact Messages" desc="Review incoming messages from residents & visitors." icon="Inbox" section="submissions" />
            </div>
        </div>
    );
};
const QuickLink = ({ title, desc, icon, section }) => {
    const I = Icons[icon] || Icons.Circle;
    return (
        <div className="surface-card flex items-start gap-4 p-6">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-brand-blue-light text-brand-blue"><I size={18} /></div>
            <div>
                <div className="font-serif text-lg text-slate-900">{title}</div>
                <div className="text-sm text-slate-500">{desc}</div>
            </div>
        </div>
    );
};

// -------- Reusable Editable Section wrapper --------
const Panel = ({ title, actions, children }) => (
    <div className="surface-card p-6">
        <div className="mb-4 flex items-center justify-between gap-3">
            <div className="font-serif text-xl text-slate-900">{title}</div>
            <div className="flex gap-2">{actions}</div>
        </div>
        {children}
    </div>
);

const Text = ({ label, value, onChange, type = "text", ...rest }) => (
    <label className="block">
        <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</span>
        <input
            type={type}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className="mt-1 w-full rounded-xl border border-slate-200 bg-white p-2.5 text-sm outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20"
            {...rest}
        />
    </label>
);

const Area = ({ label, value, onChange, rows = 4, ...rest }) => (
    <label className="block">
        <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</span>
        <textarea
            rows={rows}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className="mt-1 w-full rounded-xl border border-slate-200 bg-white p-2.5 text-sm outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20"
            {...rest}
        />
    </label>
);

const SaveButton = ({ onClick, busy, label = "Save changes" }) => (
    <button onClick={onClick} disabled={busy} className="btn-primary" data-testid="save-btn">
        <Icons.Save size={14} /> {busy ? "Saving…" : label}
    </button>
);

// -------- HOMEPAGE --------
const HomepageEditor = () => {
    const [d, setD] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => { api.get("/homepage").then((r) => setD(r.data)); }, []);
    if (!d) return <div className="text-slate-400">Loading…</div>;
    const upd = (patch) => setD({ ...d, ...patch });
    const save = async () => {
        setBusy(true);
        try { const r = await api.put("/admin/homepage", d); setD(r.data); toast.success("Homepage updated"); }
        catch (e) { toast.error("Failed"); } finally { setBusy(false); }
    };
    const setHighlight = (i, patch) => {
        const hs = [...(d.highlights || [])];
        hs[i] = { ...hs[i], ...patch };
        upd({ highlights: hs });
    };
    return (
        <div className="space-y-6">
            <Panel title="Hero Section" actions={<SaveButton onClick={save} busy={busy} />}>
                <div className="grid gap-4 md:grid-cols-2">
                    <Text label="Title" value={d.hero.title} onChange={(v) => upd({ hero: { ...d.hero, title: v } })} />
                    <Text label="Subtitle" value={d.hero.subtitle} onChange={(v) => upd({ hero: { ...d.hero, subtitle: v } })} />
                    <div className="md:col-span-2">
                        <Text label="Tagline" value={d.hero.tagline} onChange={(v) => upd({ hero: { ...d.hero, tagline: v } })} />
                    </div>
                </div>
            </Panel>
            <Panel title="Welcome" actions={<SaveButton onClick={save} busy={busy} />}>
                <div className="grid gap-4">
                    <Text label="Heading" value={d.welcome.heading} onChange={(v) => upd({ welcome: { ...d.welcome, heading: v } })} />
                    <Area label="Body" rows={5} value={d.welcome.body} onChange={(v) => upd({ welcome: { ...d.welcome, body: v } })} />
                </div>
            </Panel>
            <Panel title="Chairman's Message" actions={<SaveButton onClick={save} busy={busy} />}>
                <div className="grid gap-4 md:grid-cols-2">
                    <Text label="Name" value={d.chairman.name} onChange={(v) => upd({ chairman: { ...d.chairman, name: v } })} />
                    <Text label="Designation" value={d.chairman.designation} onChange={(v) => upd({ chairman: { ...d.chairman, designation: v } })} />
                    <div className="md:col-span-2">
                        <Area label="Message" rows={4} value={d.chairman.message} onChange={(v) => upd({ chairman: { ...d.chairman, message: v } })} />
                    </div>
                    <div>
                        <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Chairman Photo</span>
                        <div className="mt-1">
                            <FileUploader value={d.chairman.photo_id} onChange={(id) => upd({ chairman: { ...d.chairman, photo_id: id } })} accept="image/*" />
                        </div>
                    </div>
                </div>
            </Panel>
            <Panel title="Vision & Mission" actions={<SaveButton onClick={save} busy={busy} />}>
                <div className="grid gap-4 md:grid-cols-2">
                    <Area label="Vision" value={d.vision_mission.vision} onChange={(v) => upd({ vision_mission: { ...d.vision_mission, vision: v } })} />
                    <Area label="Mission" value={d.vision_mission.mission} onChange={(v) => upd({ vision_mission: { ...d.vision_mission, mission: v } })} />
                </div>
            </Panel>
            <Panel title="Highlights (6 cards)" actions={<SaveButton onClick={save} busy={busy} />}>
                <div className="grid gap-4 md:grid-cols-2">
                    {(d.highlights || []).map((h, i) => (
                        <div key={h.id} className="rounded-xl border border-slate-200 p-4">
                            <div className="grid gap-3">
                                <Text label="Icon (Lucide name)" value={h.icon} onChange={(v) => setHighlight(i, { icon: v })} />
                                <Text label="Title" value={h.title} onChange={(v) => setHighlight(i, { title: v })} />
                                <Area label="Description" rows={2} value={h.description} onChange={(v) => setHighlight(i, { description: v })} />
                            </div>
                        </div>
                    ))}
                </div>
            </Panel>
        </div>
    );
};

// -------- ABOUT --------
const AboutEditor = () => {
    const [d, setD] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => { api.get("/about").then((r) => setD(r.data)); }, []);
    if (!d) return <div className="text-slate-400">Loading…</div>;
    const save = async () => {
        setBusy(true);
        try { const r = await api.put("/admin/about", d); setD(r.data); toast.success("About updated"); }
        catch { toast.error("Failed"); } finally { setBusy(false); }
    };
    return (
        <div className="space-y-6">
            <Panel title="About / History / Vision / Mission" actions={<SaveButton onClick={save} busy={busy} />}>
                <div className="grid gap-4">
                    <Area label="About" rows={4} value={d.about} onChange={(v) => setD({ ...d, about: v })} />
                    <Area label="History" rows={4} value={d.history} onChange={(v) => setD({ ...d, history: v })} />
                    <div className="grid gap-4 md:grid-cols-2">
                        <Area label="Vision" value={d.vision} onChange={(v) => setD({ ...d, vision: v })} />
                        <Area label="Mission" value={d.mission} onChange={(v) => setD({ ...d, mission: v })} />
                    </div>
                </div>
            </Panel>
            <Panel title="Objectives" actions={<>
                <button onClick={() => setD({ ...d, objectives: [...(d.objectives || []), { id: crypto.randomUUID(), title: "", description: "" }] })} className="btn-outline !py-2"><Icons.Plus size={14} /> Add</button>
                <SaveButton onClick={save} busy={busy} />
            </>}>
                <div className="grid gap-3">
                    {(d.objectives || []).map((o, i) => (
                        <div key={o.id} className="rounded-xl border border-slate-200 p-4">
                            <div className="flex items-start gap-3">
                                <div className="grid flex-1 gap-2">
                                    <Text label="Title" value={o.title} onChange={(v) => {
                                        const arr = [...d.objectives]; arr[i] = { ...o, title: v }; setD({ ...d, objectives: arr });
                                    }} />
                                    <Area label="Description" rows={2} value={o.description} onChange={(v) => {
                                        const arr = [...d.objectives]; arr[i] = { ...o, description: v }; setD({ ...d, objectives: arr });
                                    }} />
                                </div>
                                <button onClick={() => setD({ ...d, objectives: d.objectives.filter((x) => x.id !== o.id) })} className="mt-6 rounded-full p-2 text-red-500 hover:bg-red-50"><Icons.Trash2 size={16} /></button>
                            </div>
                        </div>
                    ))}
                </div>
            </Panel>
            <Panel title="Registration" actions={<SaveButton onClick={save} busy={busy} />}>
                <div className="grid gap-4 md:grid-cols-3">
                    <Text label="Registration Number" value={d.registration_number} onChange={(v) => setD({ ...d, registration_number: v })} />
                    <Text label="Registration Date" value={d.registration_date} onChange={(v) => setD({ ...d, registration_date: v })} />
                    <Text label="Authority" value={d.registration_authority} onChange={(v) => setD({ ...d, registration_authority: v })} />
                </div>
                <div className="mt-4">
                    <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Registration Certificate</span>
                    <div className="mt-1"><FileUploader value={d.registration_certificate_id} onChange={(id) => setD({ ...d, registration_certificate_id: id })} accept="image/*,application/pdf" preview={false} /></div>
                    {d.registration_certificate_id && <a href={fileUrl(d.registration_certificate_id)} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-brand-blue hover:underline">Preview certificate ↗</a>}
                </div>
            </Panel>
        </div>
    );
};

// -------- COMMITTEE --------
const CommitteeManager = () => {
    const [rows, setRows] = useState([]);
    const [editing, setEditing] = useState(null);
    const reload = () => api.get("/committee").then((r) => setRows(r.data));
    useEffect(() => { reload(); }, []);
    const empty = () => ({ name: "", designation: "", photo_id: "", mobile: "", email: "", tenure: "", description: "", order: rows.length + 1 });
    return (
        <Panel title="Association Committee" actions={
            <button className="btn-primary" onClick={() => setEditing(empty())} data-testid="add-committee-btn"><Icons.Plus size={14} /> Add member</button>
        }>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                        <tr>
                            <th className="p-3">Order</th><th className="p-3">Name</th><th className="p-3">Designation</th><th className="p-3">Contact</th><th className="p-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((m) => (
                            <tr key={m.id} className="border-b border-slate-100" data-testid={`committee-row-${m.id}`}>
                                <td className="p-3">{m.order}</td>
                                <td className="p-3">
                                    <div className="flex items-center gap-3">
                                        {m.photo_id ? <img src={fileUrl(m.photo_id)} alt="" className="h-9 w-9 rounded-full object-cover" /> : <div className="grid h-9 w-9 place-items-center rounded-full bg-brand-blue-light text-brand-blue">{m.name.charAt(0)}</div>}
                                        <span className="font-medium text-slate-900">{m.name}</span>
                                    </div>
                                </td>
                                <td className="p-3">{m.designation}</td>
                                <td className="p-3 text-slate-600">{m.mobile}<br /><span className="text-xs text-slate-400">{m.email}</span></td>
                                <td className="p-3 text-right">
                                    <button onClick={() => setEditing(m)} className="btn-ghost !py-1" data-testid={`edit-committee-${m.id}`}><Icons.Pencil size={14} /></button>
                                    <button onClick={async () => { if (!confirm("Delete?")) return; await api.delete(`/admin/committee/${m.id}`); toast.success("Deleted"); reload(); }} className="btn-ghost !py-1 text-red-500"><Icons.Trash2 size={14} /></button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">No committee members yet.</td></tr>}
                    </tbody>
                </table>
            </div>

            <EditDialog
                open={!!editing}
                onClose={() => setEditing(null)}
                title={editing?.id ? "Edit committee member" : "Add committee member"}
                onSave={async () => {
                    try {
                        if (editing.id) await api.put(`/admin/committee/${editing.id}`, editing);
                        else await api.post("/admin/committee", editing);
                        toast.success("Saved"); setEditing(null); reload();
                    } catch (e) { toast.error("Failed"); }
                }}
            >
                {editing && (
                    <div className="grid gap-3 md:grid-cols-2">
                        <Text label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
                        <Text label="Designation" value={editing.designation} onChange={(v) => setEditing({ ...editing, designation: v })} />
                        <Text label="Mobile" value={editing.mobile} onChange={(v) => setEditing({ ...editing, mobile: v })} />
                        <Text label="Email" value={editing.email} onChange={(v) => setEditing({ ...editing, email: v })} />
                        <Text label="Tenure" value={editing.tenure} onChange={(v) => setEditing({ ...editing, tenure: v })} />
                        <Text label="Order" type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: parseInt(v) || 0 })} />
                        <div className="md:col-span-2"><Area label="Description" rows={2} value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} /></div>
                        <div className="md:col-span-2">
                            <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Photo</span>
                            <div className="mt-1"><FileUploader value={editing.photo_id} onChange={(id) => setEditing({ ...editing, photo_id: id })} accept="image/*" /></div>
                        </div>
                    </div>
                )}
            </EditDialog>
        </Panel>
    );
};

// -------- SOCIETIES --------
const SocietiesManager = () => {
    const [rows, setRows] = useState([]);
    const [editing, setEditing] = useState(null);
    const reload = () => api.get("/societies").then((r) => setRows(r.data));
    useEffect(() => { reload(); }, []);
    const empty = () => ({
        name: "", slug: "", short_description: "", about: "", registration_number: "", registration_date: "",
        registration_certificate_id: "", logo_id: "", cover_id: "", address: "", map_embed_url: "",
        buildings: 0, wings: 0, floors: 0, flats: 0, members: 0,
        facilities: [], amenities: [], contact: { address: "", phone: "", telephone: "", email: "", office_timing: "", map_embed_url: "" },
        gallery_image_ids: [], featured: false, order: rows.length + 1,
    });
    return (
        <Panel title="Member Societies" actions={<button className="btn-primary" onClick={() => setEditing(empty())} data-testid="add-society-btn"><Icons.Plus size={14} /> Add society</button>}>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                        <tr><th className="p-3">Name</th><th className="p-3">Reg No.</th><th className="p-3">Flats</th><th className="p-3">Featured</th><th className="p-3 text-right">Actions</th></tr>
                    </thead>
                    <tbody>
                        {rows.map((s) => (
                            <tr key={s.id} className="border-b border-slate-100">
                                <td className="p-3">
                                    <div className="font-medium text-slate-900">{s.name}</div>
                                    <div className="text-xs text-slate-400">/societies/{s.slug}</div>
                                </td>
                                <td className="p-3">{s.registration_number}</td>
                                <td className="p-3">{s.flats}</td>
                                <td className="p-3">{s.featured ? "Yes" : "No"}</td>
                                <td className="p-3 text-right">
                                    <button onClick={() => setEditing(s)} className="btn-ghost !py-1"><Icons.Pencil size={14} /></button>
                                    <button onClick={async () => { if (!confirm("Delete society?")) return; await api.delete(`/admin/societies/${s.id}`); toast.success("Deleted"); reload(); }} className="btn-ghost !py-1 text-red-500"><Icons.Trash2 size={14} /></button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">No societies yet.</td></tr>}
                    </tbody>
                </table>
            </div>

            <EditDialog
                open={!!editing}
                onClose={() => setEditing(null)}
                title={editing?.id ? "Edit society" : "Add society"}
                wide
                onSave={async () => {
                    try {
                        if (editing.id) await api.put(`/admin/societies/${editing.id}`, editing);
                        else await api.post("/admin/societies", editing);
                        toast.success("Saved"); setEditing(null); reload();
                    } catch { toast.error("Failed"); }
                }}
            >
                {editing && (
                    <div className="grid gap-3 md:grid-cols-2">
                        <Text label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
                        <Text label="Slug (URL)" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} />
                        <div className="md:col-span-2"><Area label="Short Description" rows={2} value={editing.short_description} onChange={(v) => setEditing({ ...editing, short_description: v })} /></div>
                        <div className="md:col-span-2"><Area label="About" rows={4} value={editing.about} onChange={(v) => setEditing({ ...editing, about: v })} /></div>
                        <Text label="Registration Number" value={editing.registration_number} onChange={(v) => setEditing({ ...editing, registration_number: v })} />
                        <Text label="Registration Date" value={editing.registration_date} onChange={(v) => setEditing({ ...editing, registration_date: v })} />
                        <Text label="Buildings" type="number" value={editing.buildings} onChange={(v) => setEditing({ ...editing, buildings: parseInt(v) || 0 })} />
                        <Text label="Wings" type="number" value={editing.wings} onChange={(v) => setEditing({ ...editing, wings: parseInt(v) || 0 })} />
                        <Text label="Floors" type="number" value={editing.floors} onChange={(v) => setEditing({ ...editing, floors: parseInt(v) || 0 })} />
                        <Text label="Flats" type="number" value={editing.flats} onChange={(v) => setEditing({ ...editing, flats: parseInt(v) || 0 })} />
                        <Text label="Members" type="number" value={editing.members} onChange={(v) => setEditing({ ...editing, members: parseInt(v) || 0 })} />
                        <Text label="Order" type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: parseInt(v) || 0 })} />
                        <div className="md:col-span-2"><Text label="Address" value={editing.address} onChange={(v) => setEditing({ ...editing, address: v })} /></div>
                        <div className="md:col-span-2"><Text label="Google Map Embed URL" value={editing.map_embed_url} onChange={(v) => setEditing({ ...editing, map_embed_url: v })} /></div>
                        <label className="flex items-center gap-2 md:col-span-2">
                            <input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} />
                            <span className="text-sm text-slate-700">Featured on Home page</span>
                        </label>
                        <div>
                            <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Logo</span>
                            <div className="mt-1"><FileUploader value={editing.logo_id} onChange={(id) => setEditing({ ...editing, logo_id: id })} accept="image/*" /></div>
                        </div>
                        <div>
                            <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Cover Image</span>
                            <div className="mt-1"><FileUploader value={editing.cover_id} onChange={(id) => setEditing({ ...editing, cover_id: id })} accept="image/*" /></div>
                        </div>
                        <div className="md:col-span-2">
                            <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Registration Certificate</span>
                            <div className="mt-1"><FileUploader value={editing.registration_certificate_id} onChange={(id) => setEditing({ ...editing, registration_certificate_id: id })} accept="image/*,application/pdf" preview={false} /></div>
                        </div>
                        <div className="md:col-span-2 rounded-xl border border-slate-200 p-4">
                            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Contact</div>
                            <div className="grid gap-3 md:grid-cols-2">
                                <Text label="Phone" value={editing.contact?.phone} onChange={(v) => setEditing({ ...editing, contact: { ...editing.contact, phone: v } })} />
                                <Text label="Email" value={editing.contact?.email} onChange={(v) => setEditing({ ...editing, contact: { ...editing.contact, email: v } })} />
                                <Text label="Office Timing" value={editing.contact?.office_timing} onChange={(v) => setEditing({ ...editing, contact: { ...editing.contact, office_timing: v } })} />
                                <Text label="Telephone" value={editing.contact?.telephone} onChange={(v) => setEditing({ ...editing, contact: { ...editing.contact, telephone: v } })} />
                            </div>
                        </div>
                        <TagListEditor label="Facilities" items={editing.facilities || []} onChange={(v) => setEditing({ ...editing, facilities: v })} />
                        <TagListEditor label="Amenities" items={editing.amenities || []} onChange={(v) => setEditing({ ...editing, amenities: v })} />
                        <div className="md:col-span-2">
                            <SocietyCommitteeInline society={editing} />
                        </div>
                        <div className="md:col-span-2">
                            <SocietyDocumentsInline society={editing} />
                        </div>
                        <div className="md:col-span-2">
                            <SocietyGalleryInline society={editing} />
                        </div>
                        <div className="md:col-span-2">
                            <SocietyEventsInline society={editing} />
                        </div>
                    </div>
                )}
            </EditDialog>
        </Panel>
    );
};

// Inline manager for a society's committee (only after society is saved)
const SocietyCommitteeInline = ({ society }) => {
    const [rows, setRows] = useState([]);
    const [editing, setEditing] = useState(null);
    const reload = () => {
        if (!society?.id) return;
        api.get(`/societies/${society.id}/committee`).then((r) => setRows(r.data));
    };
    useEffect(() => { reload(); }, [society?.id]);

    if (!society?.id) {
        return (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5 text-center text-sm text-slate-500" data-testid="society-committee-hint">
                <Icons.Users className="mx-auto mb-2 text-slate-400" size={20} />
                Save the society first, then re-open to add its committee members.
            </div>
        );
    }

    const empty = () => ({
        society_id: society.id, name: "", designation: "", photo_id: "",
        mobile: "", email: "", order: rows.length + 1,
    });
    const save = async () => {
        try {
            if (editing.id) await api.put(`/admin/society-committee/${editing.id}`, editing);
            else await api.post("/admin/society-committee", editing);
            toast.success("Member saved"); setEditing(null); reload();
        } catch { toast.error("Failed to save member"); }
    };
    const remove = async (id) => {
        if (!confirm("Remove this member?")) return;
        await api.delete(`/admin/society-committee/${id}`); toast.success("Removed"); reload();
    };

    return (
        <div className="rounded-xl border border-slate-200 p-4" data-testid="society-committee-section">
            <div className="mb-3 flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Society Committee ({rows.length})</div>
                <button type="button" onClick={() => setEditing(empty())} className="btn-outline !py-1.5 !px-4 text-xs" data-testid="add-society-committee-btn">
                    <Icons.Plus size={12} /> Add member
                </button>
            </div>
            <div className="grid gap-2">
                {rows.map((m) => (
                    <div key={m.id} className="flex items-center gap-3 rounded-lg border border-slate-100 bg-white p-3" data-testid={`society-committee-row-${m.id}`}>
                        {m.photo_id ? (
                            <img src={fileUrl(m.photo_id)} alt="" className="h-10 w-10 rounded-full object-cover" />
                        ) : (
                            <div className="grid h-10 w-10 place-items-center rounded-full bg-brand-blue-light text-sm font-medium text-brand-blue">{m.name?.charAt(0) || "?"}</div>
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-slate-900">{m.name}</div>
                            <div className="text-xs text-slate-500">{m.designation}{m.mobile ? ` · ${m.mobile}` : ""}{m.email ? ` · ${m.email}` : ""}</div>
                        </div>
                        <button type="button" onClick={() => setEditing(m)} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100" data-testid={`edit-society-committee-${m.id}`}><Icons.Pencil size={14} /></button>
                        <button type="button" onClick={() => remove(m.id)} className="rounded-lg p-1.5 text-red-500 hover:bg-red-50"><Icons.Trash2 size={14} /></button>
                    </div>
                ))}
                {rows.length === 0 && (
                    <div className="rounded-lg border border-dashed border-slate-200 py-6 text-center text-xs text-slate-500">
                        No committee members yet. Click "Add member" to get started.
                    </div>
                )}
            </div>

            <EditDialog
                open={!!editing}
                onClose={() => setEditing(null)}
                title={editing?.id ? "Edit society committee member" : "Add society committee member"}
                onSave={save}
            >
                {editing && (
                    <div className="grid gap-3 md:grid-cols-2">
                        <Text label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
                        <Text label="Designation" value={editing.designation} onChange={(v) => setEditing({ ...editing, designation: v })} />
                        <Text label="Mobile" value={editing.mobile} onChange={(v) => setEditing({ ...editing, mobile: v })} />
                        <Text label="Email" type="email" value={editing.email} onChange={(v) => setEditing({ ...editing, email: v })} />
                        <Text label="Order" type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: parseInt(v) || 0 })} />
                        <div className="md:col-span-2">
                            <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Photo</span>
                            <div className="mt-1"><FileUploader value={editing.photo_id} onChange={(id) => setEditing({ ...editing, photo_id: id })} accept="image/*" /></div>
                        </div>
                    </div>
                )}
            </EditDialog>
        </div>
    );
};

const HintPanel = ({ label, icon = "Info" }) => {
    const I = Icons[icon] || Icons.Info;
    return (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5 text-center text-sm text-slate-500">
            <I className="mx-auto mb-2 text-slate-400" size={20} />
            {label}
        </div>
    );
};

// Documents inline — bound to society_id
const SOCIETY_DOC_CATEGORIES = ["Notices", "Circulars", "AGM", "Financial Reports", "Audit Reports", "Election Notices", "Meeting Minutes", "Maintenance Notices", "Water Supply Notices", "Others"];

const SocietyDocumentsInline = ({ society }) => {
    const [rows, setRows] = useState([]);
    const [editing, setEditing] = useState(null);
    const reload = () => {
        if (!society?.id) return;
        api.get("/documents", { params: { society_id: society.id, include_hidden: true, limit: 200 } }).then((r) => setRows(r.data));
    };
    useEffect(() => { reload(); }, [society?.id]);

    if (!society?.id) return <HintPanel label="Save the society first, then re-open to upload its documents." icon="FileText" />;

    const empty = () => ({ title: "", category: "Notices", description: "", file_id: "", society_id: society.id, pinned: false, published: true });
    const save = async () => {
        try {
            if (editing.id) await api.put(`/admin/documents/${editing.id}`, editing);
            else await api.post("/admin/documents", editing);
            toast.success("Document saved"); setEditing(null); reload();
        } catch { toast.error("Failed to save document"); }
    };
    const remove = async (id) => { if (!confirm("Delete this document?")) return; await api.delete(`/admin/documents/${id}`); toast.success("Deleted"); reload(); };

    return (
        <div className="rounded-xl border border-slate-200 p-4" data-testid="society-documents-section">
            <div className="mb-3 flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Society Documents ({rows.length})</div>
                <button type="button" onClick={() => setEditing(empty())} className="btn-outline !py-1.5 !px-4 text-xs" data-testid="add-society-doc-btn">
                    <Icons.Upload size={12} /> Upload document
                </button>
            </div>
            <div className="grid gap-2">
                {rows.map((d) => (
                    <div key={d.id} className="flex items-center gap-3 rounded-lg border border-slate-100 bg-white p-3">
                        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand-blue-light text-brand-blue"><Icons.FileText size={14} /></div>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                                {d.pinned && <Icons.Pin size={10} className="text-brand-orange" />}
                                <span className="truncate text-sm font-medium text-slate-900">{d.title}</span>
                            </div>
                            <div className="text-xs text-slate-500">{d.category} · {d.published ? "Published" : "Hidden"}</div>
                        </div>
                        {d.file_id && <a href={fileUrl(d.file_id)} target="_blank" rel="noreferrer" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100" title="Open"><Icons.ExternalLink size={14} /></a>}
                        <button type="button" onClick={() => setEditing(d)} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"><Icons.Pencil size={14} /></button>
                        <button type="button" onClick={() => remove(d.id)} className="rounded-lg p-1.5 text-red-500 hover:bg-red-50"><Icons.Trash2 size={14} /></button>
                    </div>
                ))}
                {rows.length === 0 && <div className="rounded-lg border border-dashed border-slate-200 py-6 text-center text-xs text-slate-500">No documents uploaded yet.</div>}
            </div>

            <EditDialog open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? "Edit document" : "Upload document"} onSave={save}>
                {editing && (
                    <div className="grid gap-3">
                        <Text label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
                        <label className="block">
                            <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Category</span>
                            <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 bg-white p-2.5 text-sm">
                                {SOCIETY_DOC_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                            </select>
                        </label>
                        <Area label="Description" rows={2} value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} />
                        <div>
                            <span className="text-xs font-medium uppercase tracking-wider text-slate-500">File (PDF / DOC / DOCX / XLS / XLSX / ZIP / image)</span>
                            <div className="mt-1"><FileUploader value={editing.file_id} onChange={(id) => setEditing({ ...editing, file_id: id })} accept="application/pdf,image/*,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/zip" preview={false} /></div>
                        </div>
                        <div className="flex gap-6">
                            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.pinned} onChange={(e) => setEditing({ ...editing, pinned: e.target.checked })} /> Pin</label>
                            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Published</label>
                        </div>
                    </div>
                )}
            </EditDialog>
        </div>
    );
};

// Gallery inline (albums + items) — bound to society_id
const SocietyGalleryInline = ({ society }) => {
    const [albums, setAlbums] = useState([]);
    const [items, setItems] = useState({});
    const [editing, setEditing] = useState(null);
    const [openAlbum, setOpenAlbum] = useState(null);

    const reload = () => {
        if (!society?.id) return;
        api.get("/gallery/albums", { params: { society_id: society.id } }).then((r) => setAlbums(r.data));
    };
    useEffect(() => { reload(); }, [society?.id]);
    const loadItems = async (aid) => { const r = await api.get(`/gallery/albums/${aid}/items`); setItems((p) => ({ ...p, [aid]: r.data })); };

    if (!society?.id) return <HintPanel label="Save the society first, then re-open to add gallery albums." icon="Image" />;

    const empty = () => ({ title: "", category: "Society Events", description: "", cover_id: "", society_id: society.id });
    const save = async () => {
        try {
            if (editing.id) await api.put(`/admin/gallery/albums/${editing.id}`, editing);
            else await api.post("/admin/gallery/albums", editing);
            toast.success("Album saved"); setEditing(null); reload();
        } catch { toast.error("Failed"); }
    };
    const removeAlbum = async (id) => { if (!confirm("Delete album and all its items?")) return; await api.delete(`/admin/gallery/albums/${id}`); toast.success("Deleted"); reload(); };
    const addItem = async (fileId, kind) => {
        if (!fileId || !openAlbum) return;
        await api.post("/admin/gallery/items", { album_id: openAlbum.id, file_id: fileId, kind, caption: "" });
        toast.success("Added"); loadItems(openAlbum.id);
    };
    const removeItem = async (iid) => { await api.delete(`/admin/gallery/items/${iid}`); loadItems(openAlbum.id); };

    return (
        <div className="rounded-xl border border-slate-200 p-4" data-testid="society-gallery-section">
            <div className="mb-3 flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Society Gallery ({albums.length} albums)</div>
                <button type="button" onClick={() => setEditing(empty())} className="btn-outline !py-1.5 !px-4 text-xs" data-testid="add-society-album-btn">
                    <Icons.Plus size={12} /> New album
                </button>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
                {albums.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 overflow-hidden rounded-lg border border-slate-100 bg-white p-2">
                        <div className="h-12 w-16 shrink-0 overflow-hidden rounded-md bg-brand-blue-light">
                            {a.cover_id ? <img src={fileUrl(a.cover_id)} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-brand-blue"><Icons.Image size={16} /></div>}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium text-slate-900">{a.title}</div>
                            <div className="text-xs text-slate-500">{a.category}</div>
                        </div>
                        <button type="button" onClick={() => { setOpenAlbum(a); loadItems(a.id); }} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100" title="Manage items"><Icons.Images size={14} /></button>
                        <button type="button" onClick={() => setEditing(a)} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"><Icons.Pencil size={14} /></button>
                        <button type="button" onClick={() => removeAlbum(a.id)} className="rounded-lg p-1.5 text-red-500 hover:bg-red-50"><Icons.Trash2 size={14} /></button>
                    </div>
                ))}
                {albums.length === 0 && <div className="sm:col-span-2 rounded-lg border border-dashed border-slate-200 py-6 text-center text-xs text-slate-500">No albums yet.</div>}
            </div>

            <EditDialog open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? "Edit album" : "New album"} onSave={save}>
                {editing && (
                    <div className="grid gap-3">
                        <Text label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
                        <Text label="Category" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} />
                        <Area label="Description" rows={2} value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} />
                        <div>
                            <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Cover Image</span>
                            <div className="mt-1"><FileUploader value={editing.cover_id} onChange={(id) => setEditing({ ...editing, cover_id: id })} accept="image/*" /></div>
                        </div>
                    </div>
                )}
            </EditDialog>

            <Dialog open={!!openAlbum} onOpenChange={(v) => !v && setOpenAlbum(null)}>
                <DialogContent className="max-h-[80vh] max-w-3xl overflow-y-auto">
                    <DialogHeader><DialogTitle>Items — {openAlbum?.title}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div className="flex flex-wrap gap-2">
                            <FileUploader label="Add image" accept="image/*" value="" onChange={(id) => addItem(id, "image")} preview={false} testid="album-add-image" />
                            <FileUploader label="Add video" accept="video/*" value="" onChange={(id) => addItem(id, "video")} preview={false} testid="album-add-video" />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            {(items[openAlbum?.id] || []).map((it) => (
                                <div key={it.id} className="relative overflow-hidden rounded-xl border border-slate-200">
                                    {it.kind === "video" ? (
                                        <video src={fileUrl(it.file_id)} className="h-24 w-full object-cover" />
                                    ) : (
                                        <img src={fileUrl(it.file_id)} alt="" className="h-24 w-full object-cover" />
                                    )}
                                    <button type="button" onClick={() => removeItem(it.id)} className="absolute right-1 top-1 rounded-full bg-black/50 p-1 text-white"><Icons.Trash2 size={12} /></button>
                                </div>
                            ))}
                            {(items[openAlbum?.id] || []).length === 0 && <div className="col-span-3 py-8 text-center text-sm text-slate-500">No items yet.</div>}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

// Events inline — bound to society_id
const SocietyEventsInline = ({ society }) => {
    const [rows, setRows] = useState([]);
    const [editing, setEditing] = useState(null);
    const reload = () => {
        if (!society?.id) return;
        api.get("/events", { params: { society_id: society.id } }).then((r) => setRows(r.data));
    };
    useEffect(() => { reload(); }, [society?.id]);

    if (!society?.id) return <HintPanel label="Save the society first, then re-open to add its events." icon="Calendar" />;

    const empty = () => ({ title: "", description: "", date: "", venue: "", organizer: society.name || "", banner_id: "", gallery_image_ids: [], society_id: society.id });
    const save = async () => {
        try {
            if (editing.id) await api.put(`/admin/events/${editing.id}`, editing);
            else await api.post("/admin/events", editing);
            toast.success("Event saved"); setEditing(null); reload();
        } catch { toast.error("Failed"); }
    };
    const remove = async (id) => { if (!confirm("Delete event?")) return; await api.delete(`/admin/events/${id}`); toast.success("Deleted"); reload(); };

    return (
        <div className="rounded-xl border border-slate-200 p-4" data-testid="society-events-section">
            <div className="mb-3 flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Society Events ({rows.length})</div>
                <button type="button" onClick={() => setEditing(empty())} className="btn-outline !py-1.5 !px-4 text-xs" data-testid="add-society-event-btn">
                    <Icons.Plus size={12} /> New event
                </button>
            </div>
            <div className="grid gap-2">
                {rows.map((e) => (
                    <div key={e.id} className="flex items-center gap-3 rounded-lg border border-slate-100 bg-white p-3">
                        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-brand-orange-light text-brand-orange"><Icons.Calendar size={16} /></div>
                        <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium text-slate-900">{e.title}</div>
                            <div className="text-xs text-slate-500">{e.date}{e.venue ? ` · ${e.venue}` : ""}</div>
                        </div>
                        <button type="button" onClick={() => setEditing(e)} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"><Icons.Pencil size={14} /></button>
                        <button type="button" onClick={() => remove(e.id)} className="rounded-lg p-1.5 text-red-500 hover:bg-red-50"><Icons.Trash2 size={14} /></button>
                    </div>
                ))}
                {rows.length === 0 && <div className="rounded-lg border border-dashed border-slate-200 py-6 text-center text-xs text-slate-500">No events yet.</div>}
            </div>

            <EditDialog open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? "Edit event" : "New event"} onSave={save}>
                {editing && (
                    <div className="grid gap-3 md:grid-cols-2">
                        <div className="md:col-span-2"><Text label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} /></div>
                        <Text label="Date (YYYY-MM-DD)" value={editing.date} onChange={(v) => setEditing({ ...editing, date: v })} />
                        <Text label="Venue" value={editing.venue} onChange={(v) => setEditing({ ...editing, venue: v })} />
                        <div className="md:col-span-2"><Text label="Organizer" value={editing.organizer} onChange={(v) => setEditing({ ...editing, organizer: v })} /></div>
                        <div className="md:col-span-2"><Area label="Description" rows={4} value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} /></div>
                        <div className="md:col-span-2">
                            <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Banner</span>
                            <div className="mt-1"><FileUploader value={editing.banner_id} onChange={(id) => setEditing({ ...editing, banner_id: id })} accept="image/*" /></div>
                        </div>
                    </div>
                )}
            </EditDialog>
        </div>
    );
};



const TagListEditor = ({ label, items, onChange }) => {
    const [name, setName] = useState("");
    const [icon, setIcon] = useState("CheckCircle2");
    return (
        <div className="rounded-xl border border-slate-200 p-4">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
            <div className="flex flex-wrap gap-2">
                {items.map((f, i) => (
                    <span key={f.id || i} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs">
                        {f.name} <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-red-500">×</button>
                    </span>
                ))}
            </div>
            <div className="mt-3 flex gap-2">
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="flex-1 rounded-lg border border-slate-200 p-2 text-sm" />
                <input value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="Icon" className="w-32 rounded-lg border border-slate-200 p-2 text-sm" />
                <button type="button" className="btn-outline !py-2" onClick={() => { if (!name.trim()) return; onChange([...items, { id: crypto.randomUUID(), name: name.trim(), icon }]); setName(""); }}><Icons.Plus size={14} /></button>
            </div>
        </div>
    );
};

// -------- DOCUMENTS --------
const CATEGORIES = ["Notices", "Tender Notices", "Circulars", "AGM", "Financial Reports", "Audit Reports", "Election Notices", "Meeting Minutes", "Government Orders", "Maintenance Notices", "Water Supply Notices", "Others"];

const DocumentsManager = () => {
    const [rows, setRows] = useState([]);
    const [editing, setEditing] = useState(null);
    const reload = () => api.get("/documents", { params: { include_hidden: true, limit: 500 } }).then((r) => setRows(r.data));
    useEffect(() => { reload(); }, []);
    const empty = () => ({ title: "", category: "Notices", description: "", file_id: "", society_id: null, pinned: false, published: true, publish_at: "" });
    return (
        <Panel title="Documents" actions={<button className="btn-primary" onClick={() => setEditing(empty())}><Icons.Plus size={14} /> New document</button>}>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                        <tr><th className="p-3">Title</th><th className="p-3">Category</th><th className="p-3">Status</th><th className="p-3">Downloads</th><th className="p-3 text-right">Actions</th></tr>
                    </thead>
                    <tbody>
                        {rows.map((d) => (
                            <tr key={d.id} className="border-b border-slate-100">
                                <td className="p-3">
                                    <div className="flex items-center gap-2">
                                        {d.pinned && <Icons.Pin size={12} className="text-brand-orange" />}
                                        <span className="font-medium text-slate-900">{d.title}</span>
                                    </div>
                                </td>
                                <td className="p-3">{d.category}</td>
                                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${d.published ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{d.published ? "Published" : "Hidden"}</span></td>
                                <td className="p-3">{d.download_count}</td>
                                <td className="p-3 text-right">
                                    <button onClick={() => setEditing(d)} className="btn-ghost !py-1"><Icons.Pencil size={14} /></button>
                                    <button onClick={async () => { if (!confirm("Delete?")) return; await api.delete(`/admin/documents/${d.id}`); toast.success("Deleted"); reload(); }} className="btn-ghost !py-1 text-red-500"><Icons.Trash2 size={14} /></button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">No documents yet.</td></tr>}
                    </tbody>
                </table>
            </div>
            <EditDialog open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? "Edit document" : "New document"}
                onSave={async () => {
                    try {
                        if (editing.id) await api.put(`/admin/documents/${editing.id}`, editing);
                        else await api.post("/admin/documents", editing);
                        toast.success("Saved"); setEditing(null); reload();
                    } catch { toast.error("Failed"); }
                }}>
                {editing && (
                    <div className="grid gap-3">
                        <Text label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
                        <label className="block">
                            <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Category</span>
                            <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 bg-white p-2.5 text-sm">
                                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                            </select>
                        </label>
                        <Area label="Description" rows={3} value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} />
                        <div>
                            <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Document File</span>
                            <div className="mt-1"><FileUploader value={editing.file_id} onChange={(id) => setEditing({ ...editing, file_id: id })} accept="application/pdf,image/*,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/zip" preview={false} /></div>
                            {editing.file_id && <a href={fileUrl(editing.file_id)} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-brand-blue hover:underline">Preview ↗</a>}
                        </div>
                        <div className="flex gap-6">
                            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.pinned} onChange={(e) => setEditing({ ...editing, pinned: e.target.checked })} /> Pin</label>
                            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Published</label>
                        </div>
                    </div>
                )}
            </EditDialog>
        </Panel>
    );
};

// -------- GALLERY --------
const GalleryManager = () => {
    const [albums, setAlbums] = useState([]);
    const [editing, setEditing] = useState(null);
    const [items, setItems] = useState({});
    const [openAlbum, setOpenAlbum] = useState(null);
    const reload = () => api.get("/gallery/albums").then((r) => setAlbums(r.data));
    useEffect(() => { reload(); }, []);
    const loadItems = async (aid) => { const r = await api.get(`/gallery/albums/${aid}/items`); setItems((prev) => ({ ...prev, [aid]: r.data })); };

    const empty = () => ({ title: "", category: "Association Events", description: "", cover_id: "", society_id: null });

    return (
        <div className="space-y-6">
            <Panel title="Albums" actions={<button className="btn-primary" onClick={() => setEditing(empty())}><Icons.Plus size={14} /> New album</button>}>
                <div className="grid gap-4 md:grid-cols-3">
                    {albums.map((a) => (
                        <div key={a.id} className="surface-card overflow-hidden">
                            <div className="relative h-36 bg-brand-blue-light">
                                {a.cover_id && <img src={fileUrl(a.cover_id)} alt="" className="h-full w-full object-cover" />}
                            </div>
                            <div className="p-4">
                                <div className="font-serif text-lg">{a.title}</div>
                                <div className="text-xs text-slate-500">{a.category}</div>
                                <div className="mt-3 flex gap-2">
                                    <button className="btn-outline !py-1.5 flex-1" onClick={() => { setOpenAlbum(a); loadItems(a.id); }}>Manage items</button>
                                    <button className="btn-ghost !py-1.5" onClick={() => setEditing(a)}><Icons.Pencil size={14} /></button>
                                    <button className="btn-ghost !py-1.5 text-red-500" onClick={async () => { if (!confirm("Delete album?")) return; await api.delete(`/admin/gallery/albums/${a.id}`); toast.success("Deleted"); reload(); }}><Icons.Trash2 size={14} /></button>
                                </div>
                            </div>
                        </div>
                    ))}
                    {albums.length === 0 && <div className="col-span-full py-10 text-center text-slate-500">No albums yet.</div>}
                </div>
            </Panel>

            <EditDialog open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? "Edit album" : "New album"}
                onSave={async () => {
                    try {
                        if (editing.id) await api.put(`/admin/gallery/albums/${editing.id}`, editing);
                        else await api.post("/admin/gallery/albums", editing);
                        toast.success("Saved"); setEditing(null); reload();
                    } catch { toast.error("Failed"); }
                }}>
                {editing && (
                    <div className="grid gap-3">
                        <Text label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
                        <Text label="Category" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} />
                        <Area label="Description" rows={2} value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} />
                        <div>
                            <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Cover Image</span>
                            <div className="mt-1"><FileUploader value={editing.cover_id} onChange={(id) => setEditing({ ...editing, cover_id: id })} accept="image/*" /></div>
                        </div>
                    </div>
                )}
            </EditDialog>

            <Dialog open={!!openAlbum} onOpenChange={(v) => !v && setOpenAlbum(null)}>
                <DialogContent className="max-w-3xl">
                    <DialogHeader><DialogTitle>Items — {openAlbum?.title}</DialogTitle></DialogHeader>
                    <AlbumItems album={openAlbum} items={items[openAlbum?.id] || []} reload={() => loadItems(openAlbum.id)} />
                </DialogContent>
            </Dialog>
        </div>
    );
};

const AlbumItems = ({ album, items, reload }) => {
    const addItem = async (fileId, kind) => {
        await api.post("/admin/gallery/items", { album_id: album.id, file_id: fileId, kind, caption: "" });
        toast.success("Added"); reload();
    };
    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
                <FileUploader label="Add image" accept="image/*" value="" onChange={(id) => id && addItem(id, "image")} preview={false} />
                <FileUploader label="Add video" accept="video/*" value="" onChange={(id) => id && addItem(id, "video")} preview={false} />
            </div>
            <div className="grid grid-cols-3 gap-3">
                {items.map((it) => (
                    <div key={it.id} className="relative overflow-hidden rounded-xl border border-slate-200">
                        {it.kind === "video" ? (
                            <video src={fileUrl(it.file_id)} className="h-24 w-full object-cover" />
                        ) : (
                            <img src={fileUrl(it.file_id)} alt="" className="h-24 w-full object-cover" />
                        )}
                        <button onClick={async () => { await api.delete(`/admin/gallery/items/${it.id}`); reload(); }} className="absolute right-1 top-1 rounded-full bg-black/50 p-1 text-white"><Icons.Trash2 size={12} /></button>
                    </div>
                ))}
                {items.length === 0 && <div className="col-span-full py-8 text-center text-sm text-slate-500">No items yet.</div>}
            </div>
        </div>
    );
};

// -------- EVENTS --------
const EventsManager = () => {
    const [rows, setRows] = useState([]);
    const [editing, setEditing] = useState(null);
    const reload = () => api.get("/events").then((r) => setRows(r.data));
    useEffect(() => { reload(); }, []);
    const empty = () => ({ title: "", description: "", date: "", venue: "", organizer: "", banner_id: "", gallery_image_ids: [] });
    return (
        <Panel title="Events" actions={<button className="btn-primary" onClick={() => setEditing(empty())}><Icons.Plus size={14} /> New event</button>}>
            <div className="grid gap-3">
                {rows.map((e) => (
                    <div key={e.id} className="surface-card flex items-center gap-4 p-4">
                        <div className="grid h-12 w-12 place-items-center rounded-lg bg-brand-blue-light text-brand-blue"><Icons.Calendar size={18} /></div>
                        <div className="min-w-0 flex-1">
                            <div className="font-medium text-slate-900">{e.title}</div>
                            <div className="text-xs text-slate-500">{e.date} · {e.venue}</div>
                        </div>
                        <button onClick={() => setEditing(e)} className="btn-ghost !py-1.5"><Icons.Pencil size={14} /></button>
                        <button onClick={async () => { if (!confirm("Delete?")) return; await api.delete(`/admin/events/${e.id}`); toast.success("Deleted"); reload(); }} className="btn-ghost !py-1.5 text-red-500"><Icons.Trash2 size={14} /></button>
                    </div>
                ))}
                {rows.length === 0 && <div className="py-8 text-center text-slate-500">No events yet.</div>}
            </div>
            <EditDialog open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? "Edit event" : "New event"}
                onSave={async () => {
                    try {
                        if (editing.id) await api.put(`/admin/events/${editing.id}`, editing);
                        else await api.post("/admin/events", editing);
                        toast.success("Saved"); setEditing(null); reload();
                    } catch { toast.error("Failed"); }
                }}>
                {editing && (
                    <div className="grid gap-3 md:grid-cols-2">
                        <div className="md:col-span-2"><Text label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} /></div>
                        <Text label="Date (YYYY-MM-DD)" value={editing.date} onChange={(v) => setEditing({ ...editing, date: v })} />
                        <Text label="Venue" value={editing.venue} onChange={(v) => setEditing({ ...editing, venue: v })} />
                        <div className="md:col-span-2"><Text label="Organizer" value={editing.organizer} onChange={(v) => setEditing({ ...editing, organizer: v })} /></div>
                        <div className="md:col-span-2"><Area label="Description" rows={4} value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} /></div>
                        <div className="md:col-span-2">
                            <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Banner</span>
                            <div className="mt-1"><FileUploader value={editing.banner_id} onChange={(id) => setEditing({ ...editing, banner_id: id })} accept="image/*" /></div>
                        </div>
                    </div>
                )}
            </EditDialog>
        </Panel>
    );
};

// -------- NEWS --------
const NewsManager = () => {
    const [rows, setRows] = useState([]);
    const [editing, setEditing] = useState(null);
    const reload = () => api.get("/news").then((r) => setRows(r.data));
    useEffect(() => { reload(); }, []);
    const empty = () => ({ title: "", description: "", publish_date: "", featured_image_id: "", attachment_ids: [] });
    return (
        <Panel title="News & Announcements" actions={<button className="btn-primary" onClick={() => setEditing(empty())}><Icons.Plus size={14} /> New</button>}>
            <div className="grid gap-3">
                {rows.map((n) => (
                    <div key={n.id} className="surface-card flex items-center gap-4 p-4">
                        <div className="grid h-12 w-12 place-items-center rounded-lg bg-brand-orange-light text-brand-orange"><Icons.Newspaper size={18} /></div>
                        <div className="min-w-0 flex-1">
                            <div className="font-medium text-slate-900">{n.title}</div>
                            <div className="text-xs text-slate-500">{n.publish_date}</div>
                        </div>
                        <button onClick={() => setEditing(n)} className="btn-ghost !py-1.5"><Icons.Pencil size={14} /></button>
                        <button onClick={async () => { if (!confirm("Delete?")) return; await api.delete(`/admin/news/${n.id}`); toast.success("Deleted"); reload(); }} className="btn-ghost !py-1.5 text-red-500"><Icons.Trash2 size={14} /></button>
                    </div>
                ))}
                {rows.length === 0 && <div className="py-8 text-center text-slate-500">No news yet.</div>}
            </div>
            <EditDialog open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? "Edit news" : "New news"}
                onSave={async () => {
                    try {
                        if (editing.id) await api.put(`/admin/news/${editing.id}`, editing);
                        else await api.post("/admin/news", editing);
                        toast.success("Saved"); setEditing(null); reload();
                    } catch { toast.error("Failed"); }
                }}>
                {editing && (
                    <div className="grid gap-3">
                        <Text label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
                        <Area label="Description" rows={5} value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} />
                        <Text label="Publish Date (YYYY-MM-DD)" value={editing.publish_date} onChange={(v) => setEditing({ ...editing, publish_date: v })} />
                        <div>
                            <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Featured Image</span>
                            <div className="mt-1"><FileUploader value={editing.featured_image_id} onChange={(id) => setEditing({ ...editing, featured_image_id: id })} accept="image/*" /></div>
                        </div>
                    </div>
                )}
            </EditDialog>
        </Panel>
    );
};

// -------- SUBMISSIONS --------
const SubmissionsInbox = () => {
    const [rows, setRows] = useState([]);
    const reload = () => api.get("/admin/contact/submissions").then((r) => setRows(r.data));
    useEffect(() => { reload(); }, []);
    return (
        <Panel title="Contact Form Submissions">
            <div className="grid gap-3">
                {rows.map((s) => (
                    <details key={s.id} className={`surface-card p-5 ${s.is_read ? "" : "border-l-4 border-l-brand-orange"}`}>
                        <summary className="flex cursor-pointer items-center justify-between gap-4">
                            <div>
                                <div className="font-medium text-slate-900">{s.subject}</div>
                                <div className="text-xs text-slate-500">{s.name} · {s.email} · {s.mobile}</div>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-400">
                                <span>{new Date(s.created_at).toLocaleDateString()}</span>
                                {!s.is_read && <span className="rounded-full bg-brand-orange px-2 py-0.5 text-[10px] font-semibold uppercase text-white">New</span>}
                            </div>
                        </summary>
                        <div className="mt-4 whitespace-pre-wrap text-sm text-slate-600">{s.message}</div>
                        <div className="mt-4 flex gap-2">
                            <button className="btn-outline !py-1.5" onClick={async () => { await api.put(`/admin/contact/submissions/${s.id}/read`, null, { params: { is_read: !s.is_read } }); reload(); }}>{s.is_read ? "Mark unread" : "Mark read"}</button>
                            <a className="btn-ghost" href={`mailto:${s.email}?subject=Re: ${s.subject}`}>Reply <Icons.ExternalLink size={12} /></a>
                            <button className="btn-ghost text-red-500" onClick={async () => { if (!confirm("Delete?")) return; await api.delete(`/admin/contact/submissions/${s.id}`); reload(); }}>Delete</button>
                        </div>
                    </details>
                ))}
                {rows.length === 0 && <div className="py-10 text-center text-slate-500">No submissions yet.</div>}
            </div>
        </Panel>
    );
};

// -------- SETTINGS --------
const SettingsEditor = () => {
    const [d, setD] = useState(null);
    const [contact, setContact] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        api.get("/settings").then((r) => setD(r.data));
        api.get("/contact").then((r) => setContact(r.data));
    }, []);
    const saveSettings = async () => {
        setBusy(true);
        try { await api.put("/admin/settings", d); toast.success("Settings saved"); } catch { toast.error("Failed"); } finally { setBusy(false); }
    };
    const saveContact = async () => {
        setBusy(true);
        try { await api.put("/admin/contact", contact); toast.success("Contact info saved"); } catch { toast.error("Failed"); } finally { setBusy(false); }
    };
    if (!d || !contact) return <div className="text-slate-400">Loading…</div>;
    return (
        <div className="space-y-6">
            <Panel title="Site Settings" actions={<SaveButton onClick={saveSettings} busy={busy} />}>
                <div className="grid gap-4 md:grid-cols-2">
                    <Text label="Site Name" value={d.site_name} onChange={(v) => setD({ ...d, site_name: v })} />
                    <Text label="Tagline" value={d.tagline} onChange={(v) => setD({ ...d, tagline: v })} />
                    <Text label="Primary Color" value={d.primary_color} onChange={(v) => setD({ ...d, primary_color: v })} />
                    <Text label="Accent Color" value={d.accent_color} onChange={(v) => setD({ ...d, accent_color: v })} />
                    <Text label="WhatsApp Number (+91…)" value={d.whatsapp_number} onChange={(v) => setD({ ...d, whatsapp_number: v })} />
                    <Text label="Google Analytics ID" value={d.google_analytics_id} onChange={(v) => setD({ ...d, google_analytics_id: v })} />
                    <div className="md:col-span-2"><Area label="Footer Text" rows={2} value={d.footer_text} onChange={(v) => setD({ ...d, footer_text: v })} /></div>
                    <Text label="Facebook URL" value={d.social?.facebook} onChange={(v) => setD({ ...d, social: { ...d.social, facebook: v } })} />
                    <Text label="Instagram URL" value={d.social?.instagram} onChange={(v) => setD({ ...d, social: { ...d.social, instagram: v } })} />
                    <Text label="Twitter URL" value={d.social?.twitter} onChange={(v) => setD({ ...d, social: { ...d.social, twitter: v } })} />
                    <Text label="YouTube URL" value={d.social?.youtube} onChange={(v) => setD({ ...d, social: { ...d.social, youtube: v } })} />
                </div>
            </Panel>
            <Panel title="Contact Information" actions={<SaveButton onClick={saveContact} busy={busy} />}>
                <div className="grid gap-4 md:grid-cols-2">
                    <div className="md:col-span-2"><Text label="Address" value={contact.address} onChange={(v) => setContact({ ...contact, address: v })} /></div>
                    <Text label="Email" value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} />
                    <Text label="Phone" value={contact.phone} onChange={(v) => setContact({ ...contact, phone: v })} />
                    <Text label="WhatsApp Number" value={contact.whatsapp} onChange={(v) => setContact({ ...contact, whatsapp: v })} />
                    <Text label="Office Timing" value={contact.office_timing} onChange={(v) => setContact({ ...contact, office_timing: v })} />
                    <div className="md:col-span-2"><Text label="Google Map Embed URL" value={contact.map_embed_url} onChange={(v) => setContact({ ...contact, map_embed_url: v })} /></div>
                </div>
            </Panel>
        </div>
    );
};

// -------- EditDialog helper --------
const EditDialog = ({ open, onClose, title, children, onSave, wide = false }) => (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className={wide ? "max-h-[85vh] max-w-4xl overflow-y-auto" : "max-h-[85vh] overflow-y-auto"}>
            <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
            <div className="mt-2">{children}</div>
            <DialogFooter>
                <button className="btn-ghost" onClick={onClose}>Cancel</button>
                <button className="btn-primary" onClick={onSave} data-testid="dialog-save"><Icons.Save size={14} /> Save</button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
);
